import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import Bowser from 'bowser';
import React from 'react';
import { homeStyles } from '..';
import { AppContext } from '../../../contexts/app-context';
import { AuthContext } from '../../../contexts/auth-context';
import { uicolors } from '../../../theme';
import { AgtStaffPermissions } from '../../../types';
import { captureError } from '../../../utils/capture-error';
import { Chart } from './chart';
import { MobileChart } from './mobile-chart';

const detectMobile = (): boolean => {
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatform().type === 'mobile';
};

export function AgentDashboard() {
  // Context
  const {
    state: { impersonatedAgent },
  } = React.useContext(AppContext);
  const {
    state: { user, session },
  } = React.useContext(AuthContext);
  // Hooks
  const isMobile = detectMobile();

  const staffAgtNo = session?.getAccessToken().decodePayload()['myStaffForAgt'];
  const AgtNo = impersonatedAgent?.agentno || staffAgtNo || user.username;

  // Check Agent Staff Permissions
  const staffPerms =
    session?.getAccessToken().decodePayload()['cognito:groups'] || [];
  const cantView =
    staffAgtNo && !staffPerms.includes(AgtStaffPermissions['Commissions']);

  // Query - Dashboard Rankings
  const path = '/homepage/dashboard';
  const queryKey = [path, { AgtNo }];
  const query = useQuery({
    enabled: false,
    queryKey: queryKey,
    queryFn: async () => {
      const response: {
        data: any[];
      } = await API.post('DashboardAPI', path, { body: { AgtNo } });

      if (response.data) {
        const { data }: any = response;

        const categories = [
          'Recruits',
          'Onboardings',
          'IPAP',
          'GrossDeposits',
          'NewIPAgents',
          'RingFocus',
        ];
        let result: any = data;

        categories.forEach((category) => {
          result[category] = [];

          for (let i = 1; i <= 6; i++) {
            const idKey = `${category}TopID0${i}`;
            const nameKey = `${category}Name0${i}`;
            const totalKey = `${category}Total0${i}`;

            if (data[idKey]) {
              result[category].push({
                AgtNo: data[idKey],
                AgtName: data[nameKey],
                Total: data[totalKey],
              });
            }
          }
        });

        return result;
      }
      return [];
    },
    onError: (error) => captureError({ data: { error } }),
  });

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        ...homeStyles.section,
      }}
    >
      <Box
        component="h1"
        sx={{
          textAlign: 'center',
          color: uicolors.blueNav[950],
          letterSpacing: 1.1,
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        Top 5 Agents Leaderboard
        <Box sx={{ fontSize: 12 }}>(Last 30 Days)</Box>
      </Box>

      {isMobile ? (
        <Stack
          direction="row"
          spacing={0}
          alignItems="center"
          justifyContent="space-evenly"
          sx={{ flexWrap: 'wrap', gap: 1, pb: 2 }}
        >
          <MobileChart
            AgtNo={AgtNo}
            Title="Personal Recruiting"
            data={query.data || []}
            ToolTip={''}
          />
          <MobileChart
            AgtNo={AgtNo}
            Title="Personal Applications"
            data={query.data || []}
            isCurrency
            cantView={cantView}
          />
          <MobileChart
            AgtNo={AgtNo}
            Title="Personal Premium"
            data={query.data || []}
            isCurrency
          />
          <MobileChart
            AgtNo={AgtNo}
            Title="Baseshop Recruiting"
            data={query.data || []}
          />
          <MobileChart
            AgtNo={AgtNo}
            Title="Baseshop Premium"
            data={query.data || []}
          />
          <MobileChart
            AgtNo={AgtNo}
            Title="Baseshop Applications"
            data={query.data || []}
          />
        </Stack>
      ) : (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          pb={3}
          alignItems={{ xs: 'center', md: 'stretch' }}
          justifyContent="space-evenly"
          useFlexGap
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          <Chart
            AgtNo={AgtNo}
            Title="Personal Recruiting"
            data={query.data || []}
            ToolTip={''}
            isCurrency
          />
          <Chart
            AgtNo={AgtNo}
            Title="Personal Applications"
            data={query.data || []}
            isCurrency
            cantView={cantView}
          />
          <Chart
            AgtNo={AgtNo}
            Title="Personal Premium"
            data={query.data || []}
            isCurrency
          />
          <Chart
            AgtNo={AgtNo}
            Title="Baseshop Recruiting"
            data={query.data || []}
          />
          <Chart
            AgtNo={AgtNo}
            Title="Baseshop Premium"
            data={query.data || []}
          />
          <Chart
            AgtNo={AgtNo}
            Title="Baseshop Applications"
            data={query.data || []}
          />
        </Stack>
      )}
    </Paper>
  );
}
